import { useState, useEffect } from "react";
import { API } from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, WindowsLogo, AppleLogo, LinuxLogo, Clock, WarningCircle, ArrowsClockwise, Wrench as WrenchIcon } from "@phosphor-icons/react";

function triggerDownload(filename, content) {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CopyBlock({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 bg-black border border-zinc-800 p-3 rounded-md font-mono text-xs text-zinc-300 break-all overflow-x-auto">
          {value}
        </div>
        <button
          data-testid={testId}
          onClick={copy}
          className="shrink-0 w-11 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition-colors"
        >
          {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} className="text-zinc-300" />}
        </button>
      </div>
    </div>
  );
}

export function InstallerContent({ machine, onRefresh }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const safeName = (machine.name || "machine").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const runCmd = `python rdp_agent.py --server ${backendUrl} --token ${machine.pairing_token}`;

  const [now, setNow] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const paired = !!machine.device_active;
  const expiresAt = machine.bootstrap_expires_at ? new Date(machine.bootstrap_expires_at).getTime() : null;
  const remainingMs = expiresAt != null ? expiresAt - now : null;
  const expired = !paired && remainingMs != null && remainingMs <= 0;
  const mmss = () => {
    const s = Math.max(0, Math.floor(remainingMs / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh(machine);
    } finally {
      setRefreshing(false);
    }
  };

  if (expired) {
    return (
      <div className="py-6 flex flex-col items-center text-center gap-3" data-testid="installer-expired">
        <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <WarningCircle size={26} className="text-amber-400" />
        </div>
        <div>
          <div className="font-heading font-semibold text-base">This install link expired</div>
          <p className="text-sm text-zinc-400 mt-1 max-w-xs">
            For security, an install token is only valid for 15 minutes and hasn't been used yet. Generate a fresh one to install the agent on <span className="text-zinc-200">{machine.name}</span>.
          </p>
        </div>
        <Button
          data-testid="refresh-install-link"
          onClick={handleRefresh}
          disabled={refreshing || !onRefresh}
          className="bg-blue-600 hover:bg-blue-500 mt-1"
        >
          <ArrowsClockwise size={16} className="mr-1.5" /> {refreshing ? "Generating…" : "Get a new install link"}
        </Button>
      </div>
    );
  }

  const shScript = `#!/usr/bin/env bash
# Controlly agent installer for "${machine.name}"
set -e
echo "[Controlly] Installing dependencies..."
python3 -m pip install --user --upgrade websocket-client mss pillow pyautogui
echo "[Controlly] Downloading agent..."
curl -fsSL -o rdp_agent.py "${API}/agent/script"
# Clear any stale device key from a previous install so we re-pair with the new token.
rm -f device.key
echo "[Controlly] Starting agent for ${machine.name}..."
python3 rdp_agent.py --server "${backendUrl}" --token "${machine.pairing_token}"
`;

  const batScript = String.raw`@echo off
setlocal enabledelayedexpansion
echo ============================================
echo    Controlly Agent Installer
echo    Machine: ${machine.name}
echo ============================================
echo.

REM --- Ensure a WORKING Python 3 (the Windows Store "python" alias is NOT real Python) ---
set "PYEXE="
REM Prefer the py launcher (from python.org); it is immune to the Store alias
py -3 -c "import sys" >nul 2>nul && for /f "delims=" %%E in ('py -3 -c "import sys;print(sys.executable)"') do set "PYEXE=%%E"
REM Otherwise accept python ONLY if it actually runs (the Store stub fails this test)
if not defined PYEXE ( python -c "import sys" >nul 2>nul && for /f "delims=" %%E in ('python -c "import sys;print(sys.executable)"') do set "PYEXE=%%E" )
REM Or an already-installed python.org interpreter whose PATH may not have refreshed yet (re-runs)
if not defined PYEXE for /d %%D in ("%LOCALAPPDATA%\Programs\Python\Python3*") do if exist "%%D\python.exe" set "PYEXE=%%D\python.exe"
if defined PYEXE "!PYEXE!" -c "import sys" >nul 2>nul || set "PYEXE="
if not defined PYEXE (
  echo [Setup] Python was not found. Downloading and installing it automatically...
  set "PYINST=%TEMP%\controlly-python-setup.exe"
  curl -fsSL -o "!PYINST!" "https://www.python.org/ftp/python/3.12.7/python-3.12.7-amd64.exe"
  if errorlevel 1 (
    echo [ERROR] Could not download Python. Install it manually from https://www.python.org/downloads/ then re-run this installer.
    pause
    exit /b 1
  )
  echo [Setup] Installing Python quietly ^(this can take a minute^)...
  "!PYINST!" /quiet InstallAllUsers=0 PrependPath=1 Include_pip=1 Include_launcher=1 Include_test=0
  del "!PYINST!" >nul 2>nul
  REM Take python.exe from the install ROOT only. A recursive search would also match the venv
  REM template stub (...\Lib\venv\scripts\nt\python.exe), which prints "No pyvenv.cfg file".
  for /d %%D in ("%LOCALAPPDATA%\Programs\Python\Python3*") do if exist "%%D\python.exe" set "PYEXE=%%D\python.exe"
  if not defined PYEXE if exist "%ProgramFiles%\Python312\python.exe" set "PYEXE=%ProgramFiles%\Python312\python.exe"
)
REM Final check: the resolved interpreter must actually run, otherwise fail with a clear message.
if defined PYEXE "!PYEXE!" -c "import sys" >nul 2>nul || set "PYEXE="
if not defined PYEXE (
  echo [ERROR] Could not set up a working Python 3.
  echo Please install Python 3 from https://www.python.org/downloads/ ^(tick "Add python.exe to PATH"^), then run this installer again.
  pause
  exit /b 1
)
set "PYWEXE=!PYEXE:python.exe=pythonw.exe!"
if not exist "!PYWEXE!" set "PYWEXE=!PYEXE!"

set "INSTALL_DIR=%LOCALAPPDATA%\Controlly"
if not exist "%INSTALL_DIR%" mkdir "%INSTALL_DIR%"
set "LOG=%TEMP%\controlly-install.log"
echo [Setup] Using Python: "!PYEXE!"
> "!LOG!" echo === Controlly install log ===
>> "!LOG!" echo Python exe: !PYEXE!
"!PYEXE!" -V >> "!LOG!" 2>&1
echo [1/4] Installing dependencies... ^(full details logged to !LOG!^)
"!PYEXE!" -m pip install --user --upgrade pip >> "!LOG!" 2>&1
"!PYEXE!" -m pip install --user --upgrade websocket-client mss pillow pyautogui >> "!LOG!" 2>&1
if errorlevel 1 (
  echo [ERROR] Installing dependencies failed. Open this log and send it to support:
  echo    !LOG!
  pause
  exit /b 1
)
echo [2/4] Downloading agent...
curl -fsSL -o "%INSTALL_DIR%\rdp_agent.py" "${API}/agent/script"
if errorlevel 1 (
  echo [ERROR] Download failed. Check your internet connection and try again.
  pause
  exit /b 1
)
REM Clear any stale device key left by a previous install so this fresh install re-pairs
REM with the new one-time token (prevents "reinstalled but never connects").
del /f /q "%INSTALL_DIR%\device.key" >nul 2>nul
echo [3/4] Configuring background auto-start at login...
> "%INSTALL_DIR%\run.bat" echo @echo off
>> "%INSTALL_DIR%\run.bat" echo "!PYWEXE!" "%INSTALL_DIR%\rdp_agent.py" --server "${backendUrl}" --token "${machine.pairing_token}"
> "%INSTALL_DIR%\launcher.vbs" echo Set s = CreateObject("WScript.Shell")
>> "%INSTALL_DIR%\launcher.vbs" echo s.Run """%INSTALL_DIR%\run.bat""", 0, False
copy /Y "%INSTALL_DIR%\launcher.vbs" "%APPDATA%\Microsoft\Windows\Start Menu\Programs\Startup\Controlly.vbs" >nul
> "%INSTALL_DIR%\uninstall.bat" echo @echo off
>> "%INSTALL_DIR%\uninstall.bat" echo del "%APPDATA%\Microsoft\Windows\Start Menu\Programs\Startup\Controlly.vbs" 2^>nul
>> "%INSTALL_DIR%\uninstall.bat" echo taskkill /f /im pythonw.exe 2^>nul
>> "%INSTALL_DIR%\uninstall.bat" echo echo Controlly agent removed.
>> "%INSTALL_DIR%\uninstall.bat" echo pause
echo [4/4] Starting agent now (running hidden in the background)...
start "" wscript.exe "%INSTALL_DIR%\launcher.vbs"
echo.
echo Done! "${machine.name}" is connecting now and will auto-start at every login.
echo No window needs to stay open. It also survives reboots.
echo To remove it later, run:  %INSTALL_DIR%\uninstall.bat
echo.
pause
`;

  const troubleshootScript = String.raw`@echo off
setlocal enabledelayedexpansion
title Controlly Troubleshooter - ${machine.name}
echo ============================================
echo    Controlly Agent Troubleshooter
echo    Machine: ${machine.name}
echo ============================================
echo.
echo This runs the agent VISIBLY and checks each step (internet, server,
echo WebSocket) so we can see exactly what is being blocked. Nothing is hidden.
echo.

REM --- Ensure a WORKING Python 3 (ignore the Windows Store alias) ---
set "PYEXE="
py -3 -c "import sys" >nul 2>nul && for /f "delims=" %%E in ('py -3 -c "import sys;print(sys.executable)"') do set "PYEXE=%%E"
if not defined PYEXE ( python -c "import sys" >nul 2>nul && for /f "delims=" %%E in ('python -c "import sys;print(sys.executable)"') do set "PYEXE=%%E" )
if not defined PYEXE (
  echo [Setup] Python was not found. Downloading and installing it automatically...
  set "PYINST=%TEMP%\controlly-python-setup.exe"
  curl -fsSL -o "!PYINST!" "https://www.python.org/ftp/python/3.12.7/python-3.12.7-amd64.exe"
  if errorlevel 1 (
    echo [ERROR] Could not download Python. Install it from https://www.python.org/downloads/ then re-run.
    pause
    exit /b 1
  )
  echo [Setup] Installing Python quietly ^(this can take a minute^)...
  "!PYINST!" /quiet InstallAllUsers=0 PrependPath=1 Include_pip=1 Include_launcher=1 Include_test=0
  del "!PYINST!" >nul 2>nul
  for /f "delims=" %%P in ('dir /b /s "%LOCALAPPDATA%\Programs\Python\python.exe" 2^>nul') do set "PYEXE=%%P"
  if not defined PYEXE if exist "%ProgramFiles%\Python312\python.exe" set "PYEXE=%ProgramFiles%\Python312\python.exe"
)
if not defined PYEXE (
  echo [ERROR] Could not set up Python. Install Python 3 from https://www.python.org/downloads/ ^(tick "Add python.exe to PATH"^), then re-run.
  pause
  exit /b 1
)
set "INSTALL_DIR=%LOCALAPPDATA%\Controlly"
if not exist "%INSTALL_DIR%" mkdir "%INSTALL_DIR%"
echo [1/3] Installing/updating dependencies...
"!PYEXE!" -m pip install --user --upgrade websocket-client mss pillow pyautogui
echo [2/3] Downloading the latest agent...
curl -fsSL -o "%INSTALL_DIR%\rdp_agent.py" "${API}/agent/script"
if errorlevel 1 (
  echo [ERROR] Download failed. The server could not be reached over HTTPS.
  echo         This is usually a firewall / VPN / antivirus blocking the connection.
  pause
  exit /b 1
)
echo [3/3] Running connection diagnostics...
echo.
REM Clear any stale device key so diagnostics re-pair with the current install token.
del /f /q "%INSTALL_DIR%\device.key" >nul 2>nul
"!PYEXE!" "%INSTALL_DIR%\rdp_agent.py" --diagnose --server "${backendUrl}" --token "${machine.pairing_token}"
echo.
echo (If every check says PASS, close this window - your machine can reach Controlly.)
pause
`;


  return (
    <div className="space-y-4 py-2">
      {paired ? (
        <div data-testid="installer-paired-note" className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-md px-3 py-2">
          <Check size={14} /> This machine is already paired. Its install token is used up. Use "Regenerate token" if you need to re-install.
        </div>
      ) : remainingMs != null ? (
        <div data-testid="installer-countdown" className="flex items-center gap-2 text-xs bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-md px-3 py-2">
          <Clock size={14} /> One-time install link. Expires in <span className="font-mono font-semibold tabular-nums">{mmss()}</span>. Run the installer before then.
        </div>
      ) : null}
      <div>
        <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-2">Download installer</div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="download-installer-unix"
            onClick={() => triggerDownload(`controlly-install-${safeName}.sh`, shScript)}
            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 justify-start"
          >
            <AppleLogo size={16} className="mr-0.5" /><LinuxLogo size={16} className="mr-1.5" /> Mac / Linux (.sh)
          </Button>
          <Button
            data-testid="download-installer-windows"
            onClick={() => triggerDownload(`controlly-install-${safeName}.bat`, batScript)}
            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 justify-start"
          >
            <WindowsLogo size={16} className="mr-1.5" /> Windows (auto-start)
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
          <span className="text-zinc-300">Windows:</span> just double-click the .bat. It <span className="text-zinc-300">installs Python 3 automatically if it's missing</span> (it also handles the Windows Store "python" placeholder), sets up the agent, runs hidden in the background, and auto-starts at every login. <span className="text-zinc-300">Mac/Linux:</span> run <span className="font-mono text-zinc-300">bash controlly-install-{safeName}.sh</span> (Python 3 required).
        </p>
        <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
          If auto-install is blocked on a locked-down PC, install Python 3 manually from{" "}
          <a href="https://www.python.org/downloads/" target="_blank" rel="noreferrer" data-testid="python-download-link" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">python.org/downloads</a>{" "}
          (tick <span className="text-zinc-300">"Add python.exe to PATH"</span>), then run the installer again.
        </p>
      </div>

      <div className="pt-2 border-t border-white/10">
        <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-2">Not showing up? Troubleshoot</div>
        <Button
          data-testid="download-troubleshooter-windows"
          onClick={() => triggerDownload(`controlly-troubleshoot-${safeName}.bat`, troubleshootScript)}
          className="w-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 justify-start"
        >
          <WrenchIcon size={16} className="mr-1.5" /> Windows Troubleshooter (visible window)
        </Button>
        <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
          If <span className="text-zinc-300">{machine.name}</span> installed fine but never turns online, run this. It opens a <span className="text-zinc-300">visible window</span> and checks each step — internet, server (HTTPS), and the live WebSocket — so a firewall or antivirus block is obvious on screen. It also re-pairs the machine if the token is still valid.
        </p>
        <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
          <span className="text-zinc-300">Where are the logs?</span> On the remote PC, open File Explorer, paste <span className="font-mono text-zinc-300 select-all">%LOCALAPPDATA%\Controlly</span> into the address bar and press Enter, then open <span className="font-mono text-zinc-300">agent.log</span>. That file records every connect/disconnect and error.
        </p>
      </div>

      <div className="pt-2 border-t border-white/10">
        <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-2">Or run manually</div>
        <div className="space-y-3">
          <CopyBlock testId="copy-install-deps" label="1. Install dependencies" value="pip install websocket-client mss pillow pyautogui" />
          <CopyBlock testId="copy-download-agent" label="2. Download the agent" value={`curl -o rdp_agent.py ${API}/agent/script`} />
          <CopyBlock testId="copy-run-command" label="3. Run the agent" value={runCmd} />
          <CopyBlock testId="copy-pairing-token" label="Pairing token" value={machine.pairing_token} />
        </div>
      </div>
    </div>
  );
}

export function InstallerDialog({ open, onOpenChange, machine, onRefresh }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="installer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2.5">
            <img src="/controlly-icon.png" alt="Controlly" className="w-7 h-7 rounded-md" /> Install the Controlly agent
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Run this on <span className="text-zinc-200 font-medium">{machine?.name}</span>. It appears online here once connected.
          </DialogDescription>
        </DialogHeader>
        {machine && <InstallerContent machine={machine} onRefresh={onRefresh} />}
        <DialogFooter>
          <Button data-testid="close-installer" onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-500">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
